import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI } from '@/api/projects';
import { milestonesAPI } from '@/api/milestones';
import { useAuth } from '@/context/AuthContext';
import { showError, showSuccess } from '@/utils/errorHandler';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, Calendar, Flag, Layers } from 'lucide-react';
import { formatDateShort, getStageNameRussian } from '@/utils/formatters';

// Цвета для вех
const MILESTONE_COLORS = [
  { value: '#3B82F6', label: 'Синий' },
  { value: '#10B981', label: 'Зелёный' },
  { value: '#F59E0B', label: 'Жёлтый' },
  { value: '#EF4444', label: 'Красный' },
  { value: '#8B5CF6', label: 'Фиолетовый' },
  { value: '#EC4899', label: 'Розовый' },
  { value: '#6366F1', label: 'Индиго' },
  { value: '#14B8A6', label: 'Бирюзовый' },
];

// Статусы вех
const MILESTONE_STATUS_OPTIONS = [
  { value: 'planned', label: 'Запланировано', color: 'bg-gray-100 text-gray-800' },
  { value: 'in_progress', label: 'В работе', color: 'bg-blue-100 text-blue-800' },
  { value: 'completed', label: 'Завершено', color: 'bg-green-100 text-green-800' },
  { value: 'delayed', label: 'Задержка', color: 'bg-red-100 text-red-800' },
];

// Статусы этапов проекта
const STAGE_STATUS_CONFIG = {
  pending:     { label: 'Запланирован', barColor: '#9CA3AF', badgeClass: 'bg-gray-100 text-gray-700' },
  in_progress: { label: 'В работе',    barColor: '#3B82F6', badgeClass: 'bg-blue-100 text-blue-800' },
  completed:   { label: 'Завершён',    barColor: '#10B981', badgeClass: 'bg-green-100 text-green-800' },
  blocked:     { label: 'Заблокирован', barColor: '#EF4444', badgeClass: 'bg-red-100 text-red-800' },
};

const ProjectGanttPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Форма создания вехи
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    status: 'planned',
    color: '#3B82F6',
  });

  useEffect(() => {
    loadData();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectData, milestonesData] = await Promise.all([
        projectsAPI.getById(id),
        milestonesAPI.getByProject(id),
      ]);
      setProject(projectData);
      setMilestones(milestonesData);
    } catch (error) {
      showError(error, 'Ошибка загрузки данных проекта');
    } finally {
      setLoading(false);
    }
  };

  // Этапы проекта с датами
  const stages = useMemo(() => {
    if (!project?.stages) return [];
    return project.stages;
  }, [project]);

  // Вычисляем временной диапазон для Ганта (проект + этапы + вехи)
  const timeRange = useMemo(() => {
    if (!project) return { start: new Date(), end: new Date(), totalDays: 30 };

    const projectStart = new Date(project.start_date);
    const projectEnd = new Date(project.expected_end_date);

    let minDate = projectStart;
    let maxDate = projectEnd;

    // Учитываем даты этапов
    stages.forEach(s => {
      if (s.start_date) {
        const d = new Date(s.start_date);
        if (d < minDate) minDate = d;
      }
      if (s.deadline) {
        const d = new Date(s.deadline);
        if (d > maxDate) maxDate = d;
      }
    });

    // Учитываем даты вех
    milestones.forEach(m => {
      const mStart = new Date(m.start_date);
      const mEnd = new Date(m.end_date);
      if (mStart < minDate) minDate = mStart;
      if (mEnd > maxDate) maxDate = mEnd;
    });

    // Добавляем отступ
    const paddedStart = new Date(minDate);
    paddedStart.setDate(paddedStart.getDate() - 7);
    const paddedEnd = new Date(maxDate);
    paddedEnd.setDate(paddedEnd.getDate() + 7);

    const totalDays = Math.max(1, Math.ceil((paddedEnd - paddedStart) / (1000 * 60 * 60 * 24)));

    return { start: paddedStart, end: paddedEnd, totalDays };
  }, [project, stages, milestones]);

  // Позиция маркера "Сегодня"
  const todayPosition = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const offset = (today - timeRange.start) / (1000 * 60 * 60 * 24);
    const pct = (offset / timeRange.totalDays) * 100;
    if (pct < 0 || pct > 100) return null;
    return pct;
  }, [timeRange]);

  // Генерируем метки месяцев для шкалы
  const monthLabels = useMemo(() => {
    const labels = [];
    const current = new Date(timeRange.start);
    current.setDate(1);

    while (current <= timeRange.end) {
      const monthName = current.toLocaleDateString('ru-RU', { month: 'short', year: '2-digit' });
      const dayOffset = Math.ceil((current - timeRange.start) / (1000 * 60 * 60 * 24));
      const position = (dayOffset / timeRange.totalDays) * 100;

      if (position >= 0 && position <= 100) {
        labels.push({ label: monthName, position });
      }

      current.setMonth(current.getMonth() + 1);
    }

    return labels;
  }, [timeRange]);

  // Вычисляем позицию бара на таймлайне
  const calculateBarPosition = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const startOffset = Math.max(0, (start - timeRange.start) / (1000 * 60 * 60 * 24));
    const duration = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

    const left = (startOffset / timeRange.totalDays) * 100;
    const width = Math.max(1.5, (duration / timeRange.totalDays) * 100);

    return { left: `${left}%`, width: `${Math.min(width, 100 - left)}%` };
  };

  const handleCreateMilestone = async () => {
    if (!formData.name || !formData.start_date || !formData.end_date) {
      alert('Заполните обязательные поля: название, дата начала и окончания');
      return;
    }

    try {
      setCreating(true);
      await milestonesAPI.create({
        project_id: id,
        name: formData.name,
        description: formData.description || null,
        start_date: formData.start_date,
        end_date: formData.end_date,
        status: formData.status,
        color: formData.color,
        sort_order: milestones.length,
      });

      setFormData({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        status: 'planned',
        color: '#3B82F6',
      });
      setShowCreateDialog(false);
      showSuccess('Веха успешно создана');
      await loadData();
    } catch (error) {
      showError(error, 'Ошибка при создании вехи');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    if (!window.confirm('Удалить эту веху?')) return;

    try {
      setDeleting(milestoneId);
      await milestonesAPI.delete(milestoneId);
      showSuccess('Веха удалена');
      await loadData();
    } catch (error) {
      showError(error, 'Ошибка при удалении вехи');
    } finally {
      setDeleting(null);
    }
  };

  const getMilestoneStatusBadge = (status) => {
    const option = MILESTONE_STATUS_OPTIONS.find(o => o.value === status);
    return option || MILESTONE_STATUS_OPTIONS[0];
  };

  const canDelete = user?.role?.toLowerCase() === 'admin';

  // Подсчёт статистики этапов
  const stageStats = useMemo(() => {
    const withDates = stages.filter(s => s.start_date && s.deadline).length;
    const completed = stages.filter(s => s.status === 'completed').length;
    return { total: stages.length, withDates, completed };
  }, [stages]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="gantt-loading">
        Загрузка...
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center h-screen">
        Проект не найден
      </div>
    );
  }

  // Компонент шкалы времени (reusable)
  const TimeScale = () => (
    <div className="relative h-8 bg-gray-100 rounded-t-lg border-b">
      {monthLabels.map((m, idx) => (
        <div
          key={idx}
          className="absolute text-xs text-gray-600 font-medium whitespace-nowrap"
          style={{ left: `${m.position}%`, top: '50%', transform: 'translateY(-50%)' }}
        >
          {m.label}
        </div>
      ))}
      {todayPosition !== null && (
        <div
          className="absolute top-0 bottom-0 w-px bg-red-500 z-10"
          style={{ left: `${todayPosition}%` }}
        >
          <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 text-[9px] text-red-600 font-bold bg-red-50 px-1 rounded whitespace-nowrap">
            Сегодня
          </div>
        </div>
      )}
    </div>
  );

  // Компонент маркера "Сегодня" на строке бара
  const TodayMarker = () => {
    if (todayPosition === null) return null;
    return (
      <div
        className="absolute top-0 bottom-0 w-px bg-red-400/50 z-10 pointer-events-none"
        style={{ left: `${todayPosition}%` }}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="project-gantt-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-[2400px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="flex items-center gap-2"
                data-testid="back-to-project-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                Назад к проекту
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">План-график (Гант)</h1>
                <p className="text-sm text-gray-600">{project.name}</p>
              </div>
            </div>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="flex items-center gap-2"
              data-testid="add-milestone-btn"
            >
              <Plus className="w-4 h-4" />
              Добавить веху
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[2400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Информация о проекте */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Сроки проекта
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Начало:</span>
                <div className="font-semibold">{formatDateShort(project.start_date)}</div>
              </div>
              <div>
                <span className="text-gray-600">Завершение:</span>
                <div className="font-semibold">{formatDateShort(project.expected_end_date)}</div>
              </div>
              <div>
                <span className="text-gray-600">Текущий этап:</span>
                <div className="font-semibold">{getStageNameRussian(project.current_stage)}</div>
              </div>
              <div>
                <span className="text-gray-600">Этапов:</span>
                <div className="font-semibold">{stageStats.completed}/{stageStats.total} завершено</div>
              </div>
              <div>
                <span className="text-gray-600">Вех:</span>
                <div className="font-semibold">{milestones.length}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Диаграмма Ганта — Этапы проекта */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Этапы проекта
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Шкала времени */}
            <TimeScale />

            {/* Весь проект — summary bar */}
            <div className="flex items-center gap-3 py-2.5 border-b bg-indigo-50/70 px-3 mt-1">
              <div className="w-52 flex-shrink-0">
                <div className="font-semibold text-indigo-900 text-sm">Весь проект</div>
                <div className="text-xs text-indigo-600">
                  {formatDateShort(project.start_date)} — {formatDateShort(project.expected_end_date)}
                </div>
              </div>
              <div className="flex-1 relative h-7">
                <div className="absolute inset-0 bg-gray-200/60 rounded" />
                <TodayMarker />
                <div
                  className="absolute h-full bg-indigo-500 rounded shadow-sm"
                  style={calculateBarPosition(project.start_date, project.expected_end_date)}
                />
              </div>
            </div>

            {/* Строки этапов */}
            {stages.map((stage, idx) => {
              const statusCfg = STAGE_STATUS_CONFIG[stage.status] || STAGE_STATUS_CONFIG.pending;
              const hasDates = stage.start_date && stage.deadline;
              const barStyle = hasDates ? calculateBarPosition(stage.start_date, stage.deadline) : null;

              return (
                <div
                  key={stage.stage_type || idx}
                  className="flex items-center gap-3 py-2.5 border-b last:border-b-0 hover:bg-gray-50/50 transition-colors px-3"
                >
                  {/* Инфо этапа */}
                  <div className="w-52 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: statusCfg.barColor }}
                      />
                      <span className="font-medium text-sm truncate">
                        {stage.name || getStageNameRussian(stage.stage_type)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {hasDates ? (
                        <span className="text-xs text-gray-500">
                          {formatDateShort(stage.start_date)} — {formatDateShort(stage.deadline)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Даты не заданы</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Badge className={`${statusCfg.badgeClass} text-[10px] px-1.5 py-0`}>
                        {statusCfg.label}
                      </Badge>
                      {stage.progress_percent > 0 && (
                        <span className="text-[10px] text-gray-500">{stage.progress_percent}%</span>
                      )}
                    </div>
                  </div>

                  {/* Бар на таймлайне */}
                  <div className="flex-1 relative h-7">
                    <div className="absolute inset-0 bg-gray-100/70 rounded" />
                    <TodayMarker />
                    {hasDates && barStyle && (
                      <div
                        className="absolute h-full rounded shadow-sm overflow-hidden"
                        style={{ ...barStyle, backgroundColor: statusCfg.barColor }}
                      >
                        {/* Прогресс заливка */}
                        {stage.progress_percent > 0 && stage.progress_percent < 100 && (
                          <div
                            className="absolute inset-y-0 left-0 rounded-l"
                            style={{
                              width: `${stage.progress_percent}%`,
                              backgroundColor: 'rgba(255,255,255,0.3)',
                            }}
                          />
                        )}
                        {/* % текст внутри бара если помещается */}
                        {stage.progress_percent > 0 && (
                          <span className="absolute inset-0 flex items-center justify-center text-[10px] text-white font-medium drop-shadow-sm">
                            {stage.progress_percent}%
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {stages.length === 0 && (
              <div className="text-center py-8 text-gray-400 text-sm">
                Этапы проекта не созданы
              </div>
            )}
          </CardContent>
        </Card>

        {/* Вехи */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Flag className="w-5 h-5" />
              Вехи
              {milestones.length > 0 && (
                <span className="text-sm font-normal text-gray-500">({milestones.length})</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {milestones.length === 0 ? (
              <div className="text-center py-10 text-gray-500" data-testid="no-milestones">
                <Flag className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="font-medium">Вехи не созданы</p>
                <p className="text-sm mt-1">Добавьте веху для отслеживания ключевых точек проекта</p>
              </div>
            ) : (
              <>
                {/* Шкала времени для вех */}
                <TimeScale />

                <div className="mt-1" data-testid="milestones-list">
                  {milestones.map((milestone) => {
                    const statusInfo = getMilestoneStatusBadge(milestone.status);
                    const barStyle = calculateBarPosition(milestone.start_date, milestone.end_date);

                    return (
                      <div
                        key={milestone.id}
                        className="flex items-center gap-3 py-2.5 border-b last:border-b-0 hover:bg-gray-50/50 transition-colors px-3"
                        data-testid={`milestone-row-${milestone.id}`}
                      >
                        {/* Информация о вехе */}
                        <div className="w-52 flex-shrink-0">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: milestone.color || '#3B82F6' }}
                            />
                            <span className="font-medium text-sm truncate" title={milestone.name}>
                              {milestone.name}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {formatDateShort(milestone.start_date)} — {formatDateShort(milestone.end_date)}
                          </div>
                          <Badge className={`${statusInfo.color} text-[10px] px-1.5 py-0 mt-0.5`}>
                            {statusInfo.label}
                          </Badge>
                        </div>

                        {/* Бар на таймлайне */}
                        <div className="flex-1 relative h-7">
                          <div className="absolute inset-0 bg-gray-100/70 rounded" />
                          <TodayMarker />
                          <div
                            className="absolute h-full rounded shadow-sm overflow-hidden"
                            style={{
                              ...barStyle,
                              backgroundColor: milestone.color || '#3B82F6',
                              opacity: milestone.status === 'completed' ? 1 : 0.75,
                            }}
                          >
                            {milestone.progress_percent > 0 && milestone.progress_percent < 100 && (
                              <div
                                className="absolute inset-y-0 left-0 rounded-l"
                                style={{
                                  width: `${milestone.progress_percent}%`,
                                  backgroundColor: 'rgba(255,255,255,0.3)',
                                }}
                              />
                            )}
                          </div>
                        </div>

                        {/* Кнопка удаления */}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteMilestone(milestone.id)}
                            disabled={deleting === milestone.id}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                            data-testid={`delete-milestone-${milestone.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Легенда */}
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-gray-600">
          <span className="font-medium text-gray-800">Статусы этапов:</span>
          {Object.entries(STAGE_STATUS_CONFIG).map(([key, cfg]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cfg.barColor }} />
              {cfg.label}
            </div>
          ))}
          {todayPosition !== null && (
            <>
              <span className="mx-1">|</span>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-px bg-red-500" />
                Сегодня
              </div>
            </>
          )}
        </div>
      </main>

      {/* Диалог создания вехи */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md" data-testid="create-milestone-dialog">
          <DialogHeader>
            <DialogTitle>Добавить веху</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Название *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Например: Поставка оборудования"
                data-testid="milestone-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Описание</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Краткое описание вехи..."
                rows={2}
                data-testid="milestone-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Дата начала *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  data-testid="milestone-start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">Дата окончания *</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  data-testid="milestone-end-date-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Статус</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="milestone-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MILESTONE_STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Цвет</Label>
                <Select
                  value={formData.color}
                  onValueChange={(value) => setFormData({ ...formData, color: value })}
                >
                  <SelectTrigger data-testid="milestone-color-select">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: formData.color }}
                      />
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {MILESTONE_COLORS.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: c.value }}
                          />
                          {c.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              disabled={creating}
            >
              Отмена
            </Button>
            <Button
              onClick={handleCreateMilestone}
              disabled={creating}
              data-testid="save-milestone-btn"
            >
              {creating ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectGanttPage;
